// src/features/criador/cupomCriador.js
//
// Fonte ÚNICA do código de Criador Parceiro no app e na web:
//   • de onde ele vem (URL ?c= / ?cupom=, página do link /ref/{codigo},
//     rastreador legado de afiliado, deep link / install referrer no app);
//   • onde ele fica guardado enquanto a conta não existe (localStorage);
//   • como ele vira indicação (POST /afiliados/indicacao, já autenticado).
//
// O backend decide tudo o que importa (dono, autoindicação, antifraude,
// benefícios). Aqui só se guarda e se repassa o CÓDIGO — nunca percentual ou
// valor. "Um código por pessoa, para sempre" também é regra do backend: a
// segunda chamada devolve `ja_atribuida`.

import { API_BASE_URL } from "../../config/api";

const CHAVE = "LESVITA_CRIADOR_CUPOM";
// Um código guardado vale por 30 dias: depois disso a pessoa já esqueceu de
// onde veio, e prefilar um código velho confunde mais do que ajuda.
const VALIDADE_MS = 30 * 24 * 60 * 60 * 1000;

export const normalizarCodigo = (v) =>
  String(v || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 24);

/** Guarda o código (e o que já se sabe do criador) para as próximas telas. */
export function guardarCupom(codigo, extra = {}) {
  const c = normalizarCodigo(codigo);
  if (!c) return;
  try {
    localStorage.setItem(CHAVE, JSON.stringify({ codigo: c, em: Date.now(), ...extra }));
  } catch {
    /* modo privado sem storage: segue sem prefill */
  }
  // Quem já está na etapa do código (deferred link que chegou atrasado) preenche.
  try {
    window.dispatchEvent(new CustomEvent("lesvita:cupom-criador", { detail: { codigo: c } }));
  } catch { /* SSR/sem window */ }
}

export function lerCupomGuardado() {
  try {
    const d = JSON.parse(localStorage.getItem(CHAVE) || "null");
    if (!d?.codigo) return null;
    if (Date.now() - Number(d.em || 0) > VALIDADE_MS) {
      localStorage.removeItem(CHAVE);
      return null;
    }
    return d;
  } catch {
    return null;
  }
}

export function limparCupomGuardado() {
  try {
    localStorage.removeItem(CHAVE);
  } catch {
    /* nada */
  }
}

/**
 * Código de criador que chegou por algum caminho, na ordem de confiança:
 * URL atual (?c= / ?cupom=) → página do link (/ref) → rastreador legado.
 */
export function codigoDeEntrada(search = typeof window !== "undefined" ? window.location.search : "") {
  const q = new URLSearchParams(search || "");
  const daUrl = normalizarCodigo(q.get("c") || q.get("cupom"));
  if (daUrl) return { codigo: daUrl, fonte: "url", origem: q.get("origem") || "" };
  const guardado = lerCupomGuardado();
  if (guardado?.codigo) return { codigo: guardado.codigo, fonte: "link", origem: guardado.origem || "" };
  try {
    const legado = JSON.parse(localStorage.getItem("affiliateData") || "null");
    const c = normalizarCodigo(legado?.AFFILIATE_CODE);
    if (c) return { codigo: c, fonte: "legado", origem: "" };
  } catch {
    /* nada */
  }
  return null;
}

/** GET público: quem é o criador dono do código (nome, foto, benefícios). */
export async function buscarCriador(codigo) {
  const c = normalizarCodigo(codigo);
  if (!c) return { VALIDO: false };
  try {
    const r = await fetch(`${API_BASE_URL}/afiliados/publico/cupom/${encodeURIComponent(c)}`);
    return await r.json();
  } catch {
    return { VALIDO: false, REDE: true };
  }
}

/** +1 clique no funil do criador (uma vez por dia por código neste navegador). */
export function registrarClique(codigo, origem = "link") {
  const c = normalizarCodigo(codigo);
  if (!c) return;
  const chave = `LV_CRIADOR_CLIQUE_${c}`;
  const hoje = new Date().toISOString().slice(0, 10);
  try {
    if (localStorage.getItem(chave) === hoje) return;
    localStorage.setItem(chave, hoje);
  } catch {
    /* sem storage: conta mesmo assim */
  }
  fetch(`${API_BASE_URL}/afiliados/clique`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ codigo: c, origem: String(origem || "link").slice(0, 30) }),
    keepalive: true,
  }).catch(() => {});
}

/**
 * Usa o código na conta autenticada. Devolve o corpo do backend:
 *   status: atribuida | ja_atribuida | proprio | cupom_invalido |
 *           pessoa_duplicada | cadastro_incompleto
 *   criador: {NOME, FOTO_URL, INSTAGRAM} · beneficios · em_analise
 */
export async function usarCodigo(user, codigo, { origem = "APP", deviceId = "", plataforma = "" } = {}) {
  const c = normalizarCodigo(codigo);
  if (!user || !c) return { status: "cupom_invalido" };
  try {
    const token = await user.getIdToken();
    const r = await fetch(`${API_BASE_URL}/afiliados/indicacao`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ codigo: c, origem, deviceId, plataforma }),
    });
    const d = await r.json().catch(() => ({}));
    if (d?.status === "atribuida" || d?.status === "ja_atribuida") limparCupomGuardado();
    return d;
  } catch {
    return { status: "erro_rede" };
  }
}
